import { Button, Col, Flex, Input, Row, Typography } from "antd";
import to from "await-to-js";
import { t } from "i18next";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { InfoCircleFilled } from "@ant-design/icons";
import { ColorText } from "../../../components/ColorText/ColorText";
import FileUploader from "../../../components/FileUploader/FileUploader";
import { GoBackButton } from "../../../components/GoBackButton/GoBackButton";
import { MandatoryField } from "../../../components/MandatoryField/MandatoryField";
import { ModalsContext } from "../../../context/modals.context";
import { MinistryContext } from "../../../modules/ministry/ministry.context";
import { ministryService } from "../../../modules/ministry/ministry.service";
import { NapravljenZahtevZaMarkicuMFin } from "../../../modules/ministry/ministry.types";
import { ExciseStampsContext } from "../../../modules/obligee/exciseStamps/exciseStamps.context";
import { exciseStampsService } from "../../../modules/obligee/exciseStamps/exciseStamps.service";
import {
  AkciznaMarkicaKategorijaProzivoda,
  IKategorijaVrstaZahtevaDtoProps,
  NapravljenZahtevZaMarkicu,
  RequestedStampsInfoTableColumnName,
  TipObveznika,
} from "../../../modules/obligee/exciseStamps/exciseStamps.types";
import { ApiResponseObject, DocumentApi } from "../../../types";
import { downloadFile } from "../../../utils/fileDownload";
import { toastService } from "../../../utils/toast.service";
import { ForeignProducerInfo } from "../../Obligee/ExciseStamp/components/ForeignProducerInfo/ForeignProducerInfo";
import { RequestedStampsInfo } from "../../Obligee/ExciseStamp/components/RequestedStampsInfo/RequestedStampsInfo";
import { ReasonModal } from "./components/ReasonModal/ReasonModal";
import { RequestedStampsInfoTable } from "./components/RequestedStampsInfoTable/RequestedStampsInfoTable";
import { StampsForeignInfo } from "./components/StampsForeignInfo/StampsForeignInfo";
import { StampsUserInfo } from "./components/StampsUserInfo/StampsUserInfo";
import style from "./StampDetails.module.scss";

const { Text } = Typography;
interface IStampDetailsProps {
  type?: string;
}

const getIdFromUrl = () => {
  let queryParams = new URLSearchParams(window.location.search);
  const requestId = queryParams.get("id");
  return requestId ? Number(requestId) : null;
};

export const StampDetails: React.FC<IStampDetailsProps> = ({ type }) => {
  const [isFileRemoved, setIsFileRemoved] = useState(false);
  const { exciseStamps, setExciseStamps, clearPayloadState } =
    useContext(ExciseStampsContext);
  const { ministry, setMinistry } = useContext(MinistryContext);
  const { handleUpdateModals, setError } = useContext(ModalsContext);
  const [showUvoznik, setShowUvoznik] = useState<boolean>();
  const [isPP, setIsPP] = useState<boolean>();
  const [isProizvodjac, setIsProizvodjac] = useState<boolean>();
  const [isUvoznik, setIsUvoznik] = useState<boolean>();
  const isEdit = type ? exciseStamps.isVracenNaDopunu : false;

  const navigate = useNavigate();
  const [showTipPakovanja, setShowTipPakovanja] = useState(false);

  const isMfin = !type;
  const goBackPath = isMfin
    ? "/mfin/listaMarkica"
    : "/obveznik/akcizneMarkice/listaMarkica";

  const apiType = (): Promise<
    ApiResponseObject<NapravljenZahtevZaMarkicuMFin | NapravljenZahtevZaMarkicu>
  > => {
    if (type) {
      return exciseStampsService.postInformacijeNapravljenogZahtevaZaMarkicu({
        zahtevMarkicaId: exciseStamps.selectedMarkicaId || getIdFromUrl(),
      });
    }
    return ministryService.postInformacijeNapravljenogZahtevaZaMarkicu({
      zahtevMarkicaId: ministry.selectedMarkicaId || getIdFromUrl(),
    });
  };

  // Todo: Change this
  const findOpseg = (res: IKategorijaVrstaZahtevaDtoProps[]) => {
    setExciseStamps({
      isOpseg: res.some(
        (e: IKategorijaVrstaZahtevaDtoProps) => e.isporuceniOpsezi?.length
      ),
    });
  };

  const postInformacijeNapravljenogZahtevaZaMarkicu = async () => {
    const [err, res] = await to(apiType());
    if (err || !res.success) return setError(res?.errorMessage);

    findOpseg(res.data.kategorijaVrstaZahtevaDto);

    getInicijalniPodaci(
      res.data.kategorijaProizvodaId,
      res.data.tipObveznikaIds
    );

    setShowUvoznik(res.data.isUvoznik);
    setIsPP(res.data?.tipObveznikaIds?.includes(TipObveznika.POSEBNI_POSTUPAK));
    setIsProizvodjac(
      res.data?.tipObveznikaIds?.includes(TipObveznika.PROIZVODJAC)
    );
    setIsUvoznik(res.data?.tipObveznikaIds?.includes(TipObveznika.UVOZNIK));

    setShowTipPakovanja(
      res.data?.kategorijaVrstaZahtevaDto?.some(
        (kategorija) => kategorija?.tipPakovanjaShow
      )
    );

    const state = {
      isUvoznik: res.data.isUvoznik,
      evidencioniBroj: res.data.evidencioniBroj,
      brojPorudzbenice: res.data.brojPorudzbenice,
      markicaZahteviDrustvoInfoDto: res.data.markicaZahteviDrustvoInfoDto,
      //@ts-ignore
      kategorijaVrstaZahtevaDto: res.data.kategorijaVrstaZahtevaDto,
      imaPakovanje: res.data.imaPakovanje,
      showNedostajuceVrednosti: res.data.showNedostajuceVrednosti,
      isPlacen: res.data.isPlacen,
      markicaZahtevStatusNotes: res.data.markicaZahtevStatusNotes,
      zahtevZaMarkicuUvoznik: res.data.zahtevZaMarkicuUvoznik,
      markicaZahtevStatusi: res.data.markicaZahtevStatusi,
      tipObveznikaIds: res.data.tipObveznikaIds,
      statusZahteva: res.data.statusZahteva,
      stampajPredracun: res.data.stampajPredracun,
      kategorijaProizvodaId: res.data.kategorijaProizvodaId,
      dokumentSpecifikacijaDetails: res.data.dokumentSpecifikacija,
      //@ts-ignore
      isVracenNaDopunu: res.data.isVracenNaDopunu,
      // 2nd section
      inostraniProizvodjac: {
        naziv: res.data.zahtevZaMarkicuUvoznik.inostraniProizvodjacNaziv,
        poreskiBroj:
          res.data.zahtevZaMarkicuUvoznik.inostraniProizvodjacPoreskiBroj,
        adresa: res.data.zahtevZaMarkicuUvoznik.inostraniProizvodjacAdresa,
        mesto: res.data.zahtevZaMarkicuUvoznik.inostraniProizvodjacMesto,
      },
      distributer: {
        naziv:
          res.data.zahtevZaMarkicuUvoznik
            .distributerInostranogProizvodjacaNaziv,
        poreskiBroj:
          res.data.zahtevZaMarkicuUvoznik
            .distributerInostranogProizvodjacaPoreskiBroj,
        adresa:
          res.data.zahtevZaMarkicuUvoznik
            .distributerInostranogProizvodjacaAdresa,
        mesto:
          res.data.zahtevZaMarkicuUvoznik
            .distributeraInostranogProizvodjacaMesto,
      },
      adresaSlanjaMarkica:
        res.data.zahtevZaMarkicuUvoznik
          .distributerInostranogProizvodjacaAdresaSlanjaMarkica,
      // 3rd section
      // @ts-ignore
      podaciOTrazenimMarkicama: res.data.kategorijaVrstaZahtevaDto,
      podaciOTrazenimMarkicamaPrint: res.data.kategorijaVrstaZahtevaDto,
      // @ts-ignore
      showMfinNote: res?.data.showMfinNote,
      datumIsporuke: res?.data?.datumIsporuke,
    };

    //@ts-ignore
    type ? setExciseStamps(state) : setMinistry(state);
  };

  const postMfObradiZahtevZaMarkicu = async () => {
    const [err, res] = await to(
      ministryService.postMfObradiZahtevZaMarkicu({
        id: getIdFromUrl(),
        statusZahteva: ministry.markicaZahtevStatusi.odobren,
        markicaZahtevNote: null,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.ODOBREN");
    return setMinistry({
      updatedZahtevStatusId: res.data,
    });
  };

  const VratiNaDopunu = ministry.markicaZahtevStatusi.vracenNaDopunu;
  const Odbaci = ministry.markicaZahtevStatusi.odbijen;

  const handleReject = () => {
    handleUpdateModals({
      modalVisible: "reason",
      type: "odbaci",
      statusZahteva: Odbaci,
      markicaZahtevId: ministry.selectedMarkicaId,
      section: "stamps",
    });
  };
  const handleReturn = () => {
    handleUpdateModals({
      modalVisible: "reason",
      type: "vrati",
      statusZahteva: VratiNaDopunu,
      markicaZahtevId: ministry.selectedMarkicaId,
      section: "stamps",
    });
  };
  const handleAccept = () => {
    postMfObradiZahtevZaMarkicu();
    setTimeout(() => window.location.reload(), 300);
  };

  const handleEdit = () => {
    handleSendRequest();
  };

  const handlePrint = async () => {
    const [err, res] = await to(
      exciseStampsService.postGenerisiZahtevZaMarkicuPdf({
        zahtevMarkicaId:
          ministry.selectedMarkicaId ||
          exciseStamps.zahtevMarkicaId ||
          exciseStamps.selectedMarkicaId,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.DOWNLOAD_SUCCESS");
    downloadFile(res.data.content, `AM-${exciseStamps.evidencioniBroj}`, "pdf");
  };

  const showDistributerAndProducerSection = useMemo(() => {
    return Boolean(
      exciseStamps.distributer.adresa ||
        exciseStamps.distributer.mesto ||
        exciseStamps.distributer.naziv ||
        exciseStamps.distributer.poreskiBroj ||
        exciseStamps.inostraniProizvodjac.adresa ||
        exciseStamps.inostraniProizvodjac.mesto ||
        exciseStamps.inostraniProizvodjac.naziv ||
        exciseStamps.inostraniProizvodjac.poreskiBroj
    );
  }, [exciseStamps.distributer, exciseStamps.inostraniProizvodjac]);

  const isUploadSpecificationVisible = useMemo(() => {
    return (
      (isEdit ||
        ministry.dokumentSpecifikacijaDetails ||
        exciseStamps.dokumentSpecifikacijaDetails) &&
      (exciseStamps.kategorijaProizvodaId ===
        AkciznaMarkicaKategorijaProzivoda.KAFA ||
        ministry.kategorijaProizvodaId ===
          AkciznaMarkicaKategorijaProzivoda.KAFA ||
        exciseStamps.kategorijaProizvodaId ===
          AkciznaMarkicaKategorijaProzivoda.ALKOHOL ||
        ministry.kategorijaProizvodaId ===
          AkciznaMarkicaKategorijaProzivoda.ALKOHOL)
    );
  }, [
    ministry.kategorijaProizvodaId,
    exciseStamps.kategorijaProizvodaId,
    ministry.dokumentSpecifikacijaDetails,
    exciseStamps.dokumentSpecifikacijaDetails,
    isEdit,
  ]);

  const isSubmitDisabled = useMemo(() => {
    const isDistributerNotValid =
      !exciseStamps.distributer.adresa ||
      !exciseStamps.distributer.mesto ||
      !exciseStamps.distributer.naziv ||
      !exciseStamps.distributer.poreskiBroj;

    const isProizvodjacNotValid =
      !exciseStamps.inostraniProizvodjac.adresa ||
      !exciseStamps.inostraniProizvodjac.mesto ||
      !exciseStamps.inostraniProizvodjac.naziv ||
      !exciseStamps.inostraniProizvodjac.poreskiBroj;

    if (
      exciseStamps.isDistributerRequired &&
      isDistributerNotValid &&
      (showUvoznik || isPP)
    ) {
      return true;
    }

    if (
      exciseStamps.isProizvodjacRequired &&
      isProizvodjacNotValid &&
      (showUvoznik || isPP)
    ) {
      return true;
    }

    if (
      isFileRemoved &&
      !exciseStamps.dokumentSpecifikacija &&
      isUploadSpecificationVisible
    ) {
      return true;
    }

    return !exciseStamps.podaciOTrazenimMarkicama?.length;
  }, [
    isPP,
    showUvoznik,
    isFileRemoved,
    isUploadSpecificationVisible,
    exciseStamps.distributer,
    exciseStamps.inostraniProizvodjac,
    exciseStamps.isDistributerRequired,
    exciseStamps.isProizvodjacRequired,
    exciseStamps.podaciOTrazenimMarkicama,
    exciseStamps.dokumentSpecifikacija,
  ]);

  const layout = () => {
    if (type)
      return (
        <Col className={style.buttonCol} span={24}>
          {exciseStamps.isVracenNaDopunu && (
            <Button
              type="primary"
              onClick={handleEdit}
              disabled={isSubmitDisabled}
            >
              {t("COMMON.IZMENI")}
            </Button>
          )}
          <Button className="ml-12" type="primary" onClick={handlePrint}>
            {t("COMMON.PREUZMI_ZAHTEV")}
          </Button>
          {exciseStamps.stampajPredracun ? (
            <Button
              className="ml-12"
              type="primary"
              onClick={createStampajPredracun}
            >
              {t("COMMON.PREUZMI_PREDRACUN")}
            </Button>
          ) : null}
        </Col>
      );
    return (
      <Col className={style.buttonCol} span={24}>
        {ministry.isPlacen ? (
          <>
            <Button onClick={handleReject} className={style.redButton}>
              {t("COMMON.ODBIJ")}
            </Button>
            <Button onClick={handleReturn} className={style.semiRedButton}>
              {t("COMMON.VRATI_NA_DOPUNU")}
            </Button>
            <Button onClick={handleAccept} className="ml-20" type="primary">
              {t("COMMON.PRIHVATI")}
            </Button>
          </>
        ) : null}
        {ministry.stampajPredracun ? (
          <Button
            className="ml-12"
            type="primary"
            onClick={createStampajPredracun}
          >
            {t("COMMON.PREUZMI_PREDRACUN")}
          </Button>
        ) : null}
        {/* Todo: they say MINFIN shouldnt be able to print */}
        {/* <Button className="ml-20" type="primary" onClick={handlePrint}>
          {t("COMMON.STAMPAJ")}
        </Button> */}
      </Col>
    );
  };

  const getInicijalniPodaci = async (
    kategorijaProizvodaId: number,
    tipObveznikaIds: number[]
  ) => {
    if (!type) return; // dont pull data if mfin
    const [err, res] = await to(
      exciseStampsService.postVracanjeDodatnihInformacijaZaKreiranjeZahteva({
        kategorijaProizvodaId: kategorijaProizvodaId,
        tipObveznikaIds: tipObveznikaIds,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setExciseStamps({
      drustvoInformationMarkiceZahtev: res.data.drustvoInformationMarkiceZahtev,
      isUvoznik: res.data.isUvoznik,
      kategorijeZaZahtev: res.data.kategorijeZaZahtev,
    });
  };

  const putIzmeniZahtevZaMarkicu = async () => {
    const handlePayload = () => {
      const isUvoznik = exciseStamps.tipObveznikaIds.includes(2);

      return {
        brojPorudzbenice: exciseStamps.brojPorudzbenice,
        markicaKategorijaId: exciseStamps.kategorijaProizvodaId,
        markciaZahtevId: exciseStamps.selectedMarkicaId,
        tipObveznikaIds: exciseStamps.tipObveznikaIds,
        dokumentSpecifikacija: exciseStamps.dokumentSpecifikacija,
        adresaSlanjaMarkica: exciseStamps.adresaSlanjaMarkica,
        podaciOTrazenimMarkicama: exciseStamps.podaciOTrazenimMarkicama.map(
          (markica) => ({
            kolicina: markica.kolicina,
            // left keys are when we store it, right keys are info from BE
            markicaKategorijaVrsteId:
              markica.markicaKategorijaVrsteId ||
              // @ts-ignore
              markica.markicaKategorijeVrsteId,
            markicaVrstaPakovanjaId:
              // @ts-ignore
              markica.markicaVrstaPakovanjaId || markica.pakovanjeId,
            tipPakovanjaId: markica.tipPakovanjaId,
          })
        ),
        inostraniProizvodjac:
          (!exciseStamps.isProizvodjacRequired && (isUvoznik || isPP)) ||
          (!isUvoznik && !isPP)
            ? undefined
            : exciseStamps.inostraniProizvodjac,
        distributer:
          (!exciseStamps.isDistributerRequired && (isUvoznik || isPP)) ||
          (!isUvoznik && !isPP)
            ? undefined
            : exciseStamps.distributer,
      };
    };

    const [err, res] = await to(
      exciseStampsService.putIzmeniZahtevZaMarkicu(handlePayload())
    );
    if (err || !res.success) return setError(res?.errorMessage);
    clearPayloadState();

    navigate("/obveznik/akcizneMarkice/zahtevPodnet", {
      state: { isEdit: true },
    });
  };

  const handleSendRequest = () => {
    putIzmeniZahtevZaMarkicu();
  };

  const createStampajPredracun = async () => {
    const [err, res] = await to(
      exciseStampsService.createStampajPredracun(getIdFromUrl())
    );
    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.DOWNLOAD_SUCCESS");

    const evidencioniBroj =
      type && exciseStamps.evidencioniBroj
        ? exciseStamps.evidencioniBroj
        : ministry.evidencioniBroj;

    const fileName = evidencioniBroj
      ? `Predracun_${evidencioniBroj}`
      : "Predracun";

    downloadFile(res.data.content, fileName, "pdf");
  };

  const getStampType = () => {
    if (
      exciseStamps.kategorijaProizvodaId === 1 ||
      ministry.kategorijaProizvodaId === 1
    )
      return t("AKCIZNE_MARKICE.ALKOHOL");
    if (
      exciseStamps.kategorijaProizvodaId === 2 ||
      ministry.kategorijaProizvodaId === 2
    )
      return t("AKCIZNE_MARKICE.DUVAN");
    if (
      exciseStamps.kategorijaProizvodaId === 3 ||
      ministry.kategorijaProizvodaId === 3
    )
      return t("AKCIZNE_MARKICE.KAFA");
    return null;
  };

  const isUvoznikForEdit = () => {
    if (isEdit && (showUvoznik || (isPP && showDistributerAndProducerSection)))
      return <ForeignProducerInfo isUvoznik={showUvoznik} isPP={isPP} />;
    return (
      (showUvoznik || (isPP && showDistributerAndProducerSection)) && (
        <StampsForeignInfo
          exciseStampsInfo={
            type
              ? exciseStamps.zahtevZaMarkicuUvoznik
              : ministry.zahtevZaMarkicuUvoznik
          }
        />
      )
    );
  };

  const hiddenColumns = useMemo(() => {
    const columns: RequestedStampsInfoTableColumnName[] = [];

    if (
      (!exciseStamps.imaPakovanje && !ministry.imaPakovanje) ||
      (exciseStamps.kategorijaProizvodaId !==
        AkciznaMarkicaKategorijaProzivoda.KAFA &&
        ministry.kategorijaProizvodaId !==
          AkciznaMarkicaKategorijaProzivoda.KAFA)
    ) {
      columns.push("pakovanjeOznaka");
    }

    if (
      !exciseStamps.showNedostajuceVrednosti &&
      !ministry.showNedostajuceVrednosti
    ) {
      columns.push("nedostajuceVrednosti");
    }

    if (
      !exciseStamps.kategorijeZaZahtev.tipPakovanjaShow &&
      !showTipPakovanja
    ) {
      columns.push("tipPakovanja");
    }

    if (!exciseStamps.isOpseg) {
      columns.push("opseg");
      columns.push("nedostajuceVrednosti");
    }

    return columns;
  }, [
    exciseStamps.imaPakovanje,
    ministry.imaPakovanje,
    exciseStamps.isOpseg,
    exciseStamps.kategorijeZaZahtev?.tipPakovanjaShow,
    showTipPakovanja,
    exciseStamps.showNedostajuceVrednosti,
    ministry.showNedostajuceVrednosti,
    exciseStamps.kategorijaProizvodaId,
    ministry.kategorijaProizvodaId,
  ]);

  const handleDownloadDocument = async (documentId: number) => {
    const [err, res] = await to(
      exciseStampsService.downloadDocumentSpecification({
        documentId,
      })
    );

    if (err || !res.success) return setError(res?.errorMessage);

    toastService.success("TOAST.DOWNLOAD_SUCCESS");

    return res;
  };

  const handleUploadDocument = (document: DocumentApi) => {
    setIsFileRemoved(false);

    setExciseStamps({ dokumentSpecifikacija: document });
  };

  const handleRemoveDocument = () => {
    setIsFileRemoved(true);

    setExciseStamps({
      dokumentSpecifikacija: null,
    });
  };

  const initialDocument = useMemo(() => {
    return isMfin
      ? ministry.dokumentSpecifikacijaDetails
      : exciseStamps.dokumentSpecifikacijaDetails;
  }, [
    isMfin,
    ministry.dokumentSpecifikacijaDetails,
    exciseStamps.dokumentSpecifikacijaDetails,
  ]);

  useEffect(() => {
    postInformacijeNapravljenogZahtevaZaMarkicu();
  }, []);

  return (
    <Row className={style.mainContainer}>
      <ReasonModal />
      <GoBackButton
        routePath={goBackPath}
        options={{ state: { usePersistedFilters: true } }}
      />
      <Col span={24}>
        <h2 className={style.title}>
          {t("AKCIZNE_MARKICE.ZAHTEV_ZA_IZDAVANJE_KONTROLNIH_AKCIZNIH_MARKICA")}{" "}
          {getStampType()}
        </h2>
        {ministry.showMfinNote && (
          <h3 className={style.title}>
            {ColorText(t("AKCIZNE_MARKICE.NOTE"))}
          </h3>
        )}
        <h3 className={style.title}>
          {ColorText(exciseStamps.statusZahteva || ministry.statusZahteva)}
        </h3>
        {exciseStamps.datumIsporuke || ministry.datumIsporuke ? (
          <Flex gap={3}>
            <span className={style.fontSize}>
              {" "}
              {t("AKCIZNE_MARKICE.DATUM_ISPORUKE")}:
            </span>
            <strong className={style.title}>
              {exciseStamps.datumIsporuke
                ? exciseStamps.datumIsporuke
                : ministry.datumIsporuke}
            </strong>
          </Flex>
        ) : (
          <></>
        )}
        {exciseStamps?.markicaZahtevStatusNotes?.length ? (
          <h3 className={style.prekidVazenjaNote}>
            {exciseStamps.markicaZahtevStatusNotes}
          </h3>
        ) : null}
        {ministry?.markicaZahtevStatusNotes?.length ? (
          <h3 className={style.prekidVazenjaNote}>
            {ministry.markicaZahtevStatusNotes}
          </h3>
        ) : null}
      </Col>
      <Col className={style.numberCol} span={24}>
        {t("AKCIZNE_MARKICE.EVIDENCIONI_BROJ")}: {ministry.evidencioniBroj}
        {exciseStamps.evidencioniBroj}
      </Col>
      <div className={style.contentWrapper}>
        <StampsUserInfo
          exciseStampsInfo={
            type
              ? exciseStamps.markicaZahteviDrustvoInfoDto
              : ministry.markicaZahteviDrustvoInfoDto
          }
        />
        {/* {isEdit && exciseStamps.isUvoznik ? (
          <ForeignProducerInfo />
        ) : (
          exciseStamps.isUvoznik && (
            <StampsForeignInfo
              exciseStampsInfo={
                type
                  ? exciseStamps.zahtevZaMarkicuUvoznik
                  : ministry.zahtevZaMarkicuUvoznik
              }
            />
          )
        )} */}
        {isUvoznikForEdit()}
        {isEdit ? (
          <Col span={24}>
            <div className={style.title2}>
              {t("AKCIZNE_MARKICE.PODACI_O_PORUDZBENICI")}
            </div>
            <Row className={style.row}>
              <Col className="yCenter" span={5}>
                <span className={style.fontSize}>
                  {t("AKCIZNE_MARKICE.BROJ_PORUDZBENICE")}:
                </span>
              </Col>
              <Col span={5}>
                <Input
                  name="broj-porudzbenice-markice"
                  className={style.fontSize}
                  value={exciseStamps.brojPorudzbenice}
                  onChange={(e) =>
                    setExciseStamps({ brojPorudzbenice: e.target.value })
                  }
                />
              </Col>
              <Col span={7}></Col>
              <Col span={5}></Col>
            </Row>
          </Col>
        ) : (
          <Col span={24}>
            <div className={style.title2}>
              {t("AKCIZNE_MARKICE.PODACI_O_PORUDZBENICI")}
            </div>
            <Row className={style.row}>
              <Col className="yCenter" span={5}>
                <span className={style.fontSize}>
                  {t("AKCIZNE_MARKICE.BROJ_PORUDZBENICE")}:
                </span>
              </Col>
              <Col span={5}>
                {exciseStamps.brojPorudzbenice || ministry.brojPorudzbenice}
              </Col>
              <Col span={7}></Col>
              <Col span={5}></Col>
            </Row>
          </Col>
        )}
        {isUploadSpecificationVisible ? (
          <Col span={24}>
            <div className={style.title2}>
              {t(
                "AKCIZNE_MARKICE.INFORMACIJE_O_SPECIFIKACIJI_KOLICINE_TRAZENIH_MARKICA"
              )}
            </div>
            <Row className={style.row}>
              <Col className="yCenter" span={5}>
                <span className={style.fontSize}>
                  {MandatoryField("AKCIZNE_MARKICE.DOKUMENT")}
                </span>
              </Col>

              <FileUploader
                onRemove={handleRemoveDocument}
                onUpload={handleUploadDocument}
                onDownload={handleDownloadDocument}
                initialDocument={initialDocument}
                isDisabled={isMfin || !isEdit}
              />
            </Row>
            {isEdit ? (
              <Flex align="center" gap={8} className={style.uploadInfo}>
                <InfoCircleFilled className={style.infoIcon} />
                <Text className={`${style.uploadInfoText} ${style.fontSize}`}>
                  {exciseStamps?.kategorijaProizvodaId ===
                  AkciznaMarkicaKategorijaProzivoda.KAFA
                    ? t("AKCIZNE_MARKICE.UPLOAD_SPECIFIKACIJE_KAFA_INFO")
                    : t("AKCIZNE_MARKICE.UPLOAD_SPECIFIKACIJE_ALKOHOL_INFO")}
                </Text>
              </Flex>
            ) : (
              <> </>
            )}
          </Col>
        ) : (
          <></>
        )}
        {isEdit ? (
          <>
            <RequestedStampsInfo
              title={getStampType() as any}
              requestedStampsInfo={exciseStamps.podaciOTrazenimMarkicamaPrint}
            />
          </>
        ) : (
          <RequestedStampsInfoTable
            expandable={
              type
                ? exciseStamps.showNedostajuceVrednosti
                : ministry.showNedostajuceVrednosti
            }
            requestedStampsInfo={
              type
                ? exciseStamps.kategorijaVrstaZahtevaDto
                : ministry.kategorijaVrstaZahtevaDto
            }
            hiddenColumns={hiddenColumns}
            title={t("AKCIZNE_MARKICE.PODACI_O_TRAZENIM_AKCIZNIM_MARKICAMA")}
          />
        )}
      </div>
      {layout()}
    </Row>
  );
};
